import { Loader2, Users } from "lucide-react";
import type { MembershipService, RoleOption } from "@/types";
import { TeamIcon, UserIcon } from "./MemberAvatars";
import { MemberRow } from "./MemberRow";
import { MemberSearchBox } from "./MemberSearchBox";
import { useMembershipManager } from "./useMembershipManager";

export interface MembershipManagerProps {
  service: MembershipService;
  entityUuid: string;
  /** Domain-specific role dropdown copy. */
  roleOptions: RoleOption[];
  /** One-line explanation of what the roles mean for this entity. */
  description: string;
  /** Console/error label used when loading members fails. */
  loadErrorLabel: string;
  /** Optional banner rendered above the add box (e.g. agent extension warning). */
  banner?: React.ReactNode;
  /** Optional hooks fired after a successful add (e.g. agent impact check). */
  onAfterAddUser?: (userUuid: string) => void;
  onAfterAddTeam?: (teamUuid: string) => void;
  /**
   * Optional Tailwind classes applied to the member-list container so it can
   * scroll independently (e.g. inside a fixed-height modal). When set, the
   * add-member search box stays outside the scroll region so its suggestions
   * dropdown is not clipped. Omit to keep the list un-capped (default).
   */
  listScrollClass?: string;
}

/**
 * Reusable "People & teams with access" surface for any role-based entity.
 * Composes the shared search box + member rows over ``useMembershipManager``.
 * Domain specifics (copy, extension warnings) are supplied via props/slots.
 */
export function MembershipManager({
  service,
  entityUuid,
  roleOptions,
  description,
  loadErrorLabel,
  banner,
  onAfterAddUser,
  onAfterAddTeam,
  listScrollClass,
}: Readonly<MembershipManagerProps>) {
  const m = useMembershipManager({ service, entityUuid, loadErrorLabel, onAfterAddUser, onAfterAddTeam });

  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Users className="w-4 h-4 text-content-tertiary" />
          <h3 className="text-sm font-semibold text-content-primary">People &amp; teams with access</h3>
        </div>
        <p className="text-xs text-content-quaternary">{description}</p>
      </div>

      {m.error && (
        <div className="px-3 py-2 bg-red-500/10 border border-red-500/20 rounded-lg text-sm text-red-400">
          {m.error}
        </div>
      )}
      {banner}

      {m.canManage && (
        <MemberSearchBox
          query={m.query}
          setQuery={m.setQuery}
          showResults={m.showResults}
          setShowResults={m.setShowResults}
          isSearching={m.isSearching}
          teamResults={m.teamResults}
          userResults={m.userResults}
          busyId={m.busyId}
          onAddTeam={m.addTeam}
          onAddUser={m.addUser}
        />
      )}

      <div
        className={`rounded-lg border border-border-secondary divide-y divide-border-secondary/60${
          listScrollClass ? ` ${listScrollClass}` : ""
        }`}
      >
        {m.isLoading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="w-5 h-5 text-brand-primary animate-spin" />
          </div>
        ) : (
          <>
            {m.teamMembers.map((tm) => (
              <MemberRow
                key={`team-${tm.teamUuid}`}
                icon={<TeamIcon />}
                title={tm.teamName}
                subtitle="Team"
                role={tm.role}
                roleOptions={roleOptions}
                canManage={m.canManage}
                busy={m.busyId === tm.teamUuid}
                onRoleChange={(r) => m.changeTeamRole(tm.teamUuid, r)}
                onRemove={() => m.removeTeam(tm.teamUuid)}
              />
            ))}
            {m.userMembers.map((um) => (
              <MemberRow
                key={`user-${um.user.uuid}`}
                icon={<UserIcon name={um.user.name} />}
                title={um.user.name}
                subtitle={um.user.email}
                role={um.role}
                roleOptions={roleOptions}
                canManage={m.canManage}
                busy={m.busyId === um.user.uuid}
                onRoleChange={(r) => m.changeUserRole(um.user.uuid, r)}
                onRemove={() => m.removeUser(um.user.uuid)}
              />
            ))}
            {m.teamMembers.length === 0 && m.userMembers.length === 0 && (
              <div className="px-3 py-4 text-sm text-content-quaternary">No members yet.</div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
